import React, { useEffect, useState } from "react";
import { resolveVisitorCount, VISITOR_REFRESH_EVENT } from "../lib/visitorCount";
import { useT } from "../i18n";

const MIN_DIGITS = 6;

// Public visitor count in the footer. Shows loading dots, then the shared number.
// If the Apps Script URL is missing or the request fails, it keeps any cached
// number (or an em-dash) and a short "Counter connecting" note — it does not
// look broken and it does not invent a large figure.

export default function VisitorCounter() {
  const { t } = useT();
  // Blank until this page's action=get returns. A remembered total is never painted.
  const [count, setCount] = useState(null);
  const [phase, setPhase] = useState("loading");

  useEffect(() => {
    let live = true;
    let generation = 0;

    const run = () => {
      const id = ++generation;
      resolveVisitorCount().then(({ count: n, authoritative, connected }) => {
        if (!live || id !== generation) return;
        if (authoritative && n != null) {
          setCount(n);
          setPhase("ready");
          return;
        }
        setCount(null);
        setPhase(connected ? "ready" : "connecting");
      });
    };

    run();
    window.addEventListener(VISITOR_REFRESH_EVENT, run);
    return () => {
      live = false;
      window.removeEventListener(VISITOR_REFRESH_EVENT, run);
    };
  }, []);

  const waiting = phase === "loading" && count == null;
  const tiles = waiting
    ? Array(MIN_DIGITS).fill("·")
    : count == null
      ? ["—"]
      : String(count).padStart(MIN_DIGITS, "0").split("");

  return (
    <div data-testid="visitor-counter" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      <div style={{ display: "flex", gap: 3 }} aria-live="polite" aria-label={t("f.visitors")}>
        {tiles.map((d, i) => (
          <div
            key={i}
            style={{
              minWidth: 26,
              height: 44,
              padding: "0 6px",
              background: "#fff",
              color: "#1C1917",
              fontWeight: 800,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
              boxShadow: "inset 0 -3px 0 #FACC15",
            }}
          >
            {d}
          </div>
        ))}
      </div>
      {phase === "connecting" && (
        <div data-testid="visitor-counter-status" style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.2, color: "rgba(255,255,255,0.88)" }}>
          {t("f.counterConnecting")}
        </div>
      )}
    </div>
  );
}
